import { useState, useMemo } from 'react'
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@/components/catalyst/dialog'
import Button from '@/components/ui/Button'
import FormCheckbox from '@/components/ui/form/FormCheckbox'
import { DASHBOARD_COLUMNS, COLUMN_GROUPS, FIXED_COLUMNS } from './columns'

interface ColumnChooserModalProps {
  open: boolean
  onClose: () => void
  hiddenColumns: Set<string>
  onHiddenColumnsChange: (hidden: Set<string>) => void
}

export default function ColumnChooserModal({
  open,
  onClose,
  hiddenColumns,
  onHiddenColumnsChange,
}: ColumnChooserModalProps) {
  const [search, setSearch] = useState('')

  // Group columns by their group property
  const groupedColumns = useMemo(() => {
    const groups: Record<string, { name: string; title: string }[]> = {}
    for (const col of DASHBOARD_COLUMNS) {
      // Fixed columns (id, po_number) are always visible, skip them
      if (FIXED_COLUMNS.includes(col.name)) continue
      const group = col.group || 'Other'
      if (!groups[group]) groups[group] = []
      groups[group].push({ name: col.name, title: col.title })
    }
    return groups
  }, [])

  // Filter columns by search
  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groupedColumns
    const q = search.toLowerCase()
    const result: Record<string, { name: string; title: string }[]> = {}
    for (const [group, cols] of Object.entries(groupedColumns)) {
      const filtered = cols.filter(
        c => c.title.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)
      )
      if (filtered.length > 0) result[group] = filtered
    }
    return result
  }, [search, groupedColumns])

  // Ordered group names
  const orderedGroups = useMemo(() => {
    return COLUMN_GROUPS.filter(g => filteredGroups[g])
  }, [filteredGroups])

  const toggleColumn = (colName: string) => {
    const next = new Set(hiddenColumns)
    if (next.has(colName)) {
      next.delete(colName)
    } else {
      next.add(colName)
    }
    onHiddenColumnsChange(next)
  }

  const showAll = () => {
    onHiddenColumnsChange(new Set())
  }

  const hideDefaults = () => {
    const defaults = DASHBOARD_COLUMNS.filter(c => c.hidden).map(c => c.name)
    onHiddenColumnsChange(new Set(defaults))
  }

  // Count visible (non-fixed) columns
  const toggleableCount = DASHBOARD_COLUMNS.filter(c => !FIXED_COLUMNS.includes(c.name)).length
  const visibleCount = toggleableCount - [...hiddenColumns].filter(
    name => !FIXED_COLUMNS.includes(name)
  ).length

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <DialogTitle>Column Visibility</DialogTitle>

      <DialogBody data-testid="column-chooser-modal">
        {/* Search + quick actions */}
        <div className="flex items-center gap-3">
          <input
            data-testid="column-chooser-search"
            type="text"
            placeholder="Search columns..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 rounded-lg border border-zinc-200 px-3 py-1.5 text-sm focus:border-indigo-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
          />
          <span data-testid="visible-count" className="whitespace-nowrap text-xs text-zinc-500 dark:text-zinc-400">
            {visibleCount} / {toggleableCount} visible
          </span>
        </div>

        <div className="mt-3 flex gap-2">
          <Button data-testid="column-show-all" variant="secondary" size="xs" onClick={showAll}>
            Show All
          </Button>
          <Button data-testid="column-reset-defaults" variant="secondary" size="xs" onClick={hideDefaults}>
            Reset to Defaults
          </Button>
        </div>

        {/* Grouped columns */}
        <div className="mt-4 max-h-[60vh] space-y-4 overflow-y-auto">
          {orderedGroups.map(group => (
            <div key={group}>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {group}
              </h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                {filteredGroups[group].map(col => {
                  const isVisible = !hiddenColumns.has(col.name)
                  return (
                    <div
                      key={col.name}
                      data-testid={`column-toggle-${col.name}`}
                      onClick={() => toggleColumn(col.name)}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-sm text-zinc-950 hover:bg-zinc-950/5 dark:text-white dark:hover:bg-white/5"
                    >
                      <span onClick={e => e.stopPropagation()}>
                        <FormCheckbox
                          checked={isVisible}
                          onChange={() => toggleColumn(col.name)}
                        />
                      </span>
                      <span className="truncate">{col.title}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

          {orderedGroups.length === 0 && (
            <p className="py-4 text-center text-sm text-zinc-500 dark:text-zinc-400">
              No columns match your search
            </p>
          )}
        </div>
      </DialogBody>

      <DialogActions>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
